import axios from "axios";
import { ChartData, Chart as ChartJS, ChartOptions } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useRef, useState } from "react";
import { Chart } from 'react-chartjs-2';
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { APP_BASE_URL, GET_ALL_ASSETS, GET_DEVICE_TYPE_ASSET } from "../../Common/EndPoints";
import { selectedCustomersSelector } from "../../features/authentication/auth-slice";
import "./asset-widget.scss";
import { useNavigate } from "react-router-dom";
import { updateSelectedSidebarKey } from "../NewSidebar/sidebarSlice";
ChartJS.register(ChartDataLabels);

enum AssetTypeEnum {
    "All assets" = "AllAssets",
    "Maintenance Started" = "MaintenanceStarted",
}

const colors = {
    [AssetTypeEnum["All assets"]]: 'rgba(54,162,235,0.8)',
    [AssetTypeEnum["Maintenance Started"]]: 'rgba(75,192,192,0.8)',
}

const getMainChartData = (assetData: any) => {
    const data = assetData || [];
    const chartData: ChartData<"bar"> = {
        labels: data.map((item: any) => item.customerName),
        datasets: [
            {
                label: 'All assets',
                data: data.map((item: any) => item.allDevices),
                backgroundColor: colors[AssetTypeEnum["All assets"]],
                barThickness: "flex",
            },
            {
                label: 'Maintenance Started',
                data: data.map((item: any) => item.maintenanceStarted),
                backgroundColor: colors[AssetTypeEnum["Maintenance Started"]],
                barThickness: "flex",
            }
        ]
    }
    return chartData;
}

// const initialAssetData = [
//     {
//         "customerName": "DER",
//         "allDevices": 181,
//         "maintenanceStarted": 177
//     },
//     {
//         "customerName": "MIU",
//         "allDevices": 793,
//         "maintenanceStarted": 784
//     }
// ]

// const initialAssetForStation = [
//     {
//         "deviceType": "Battery",
//         "countOfAllDeviceType": 37,
//         "countOfMaintenanceStarted": 35
//     },
//     {
//         "deviceType": "BTS Card Rack",
//         "countOfAllDeviceType": 292,
//         "countOfMaintenanceStarted": 289
//     },
//     {
//         "deviceType": "Earthing",
//         "countOfAllDeviceType": 19,
//         "countOfMaintenanceStarted": 19
//     },
//     {
//         "deviceType": "FDMS",
//         "countOfAllDeviceType": 445,
//         "countOfMaintenanceStarted": 441
//     }
// ];

const getAssetTypeLabel = (assetType: string) => {
    switch (assetType) {
        case AssetTypeEnum["All assets"]:
            return "All assets";
        case AssetTypeEnum["Maintenance Started"]:
            return "Maintenance started";
        default:
            return "";
    }
}

const getChildChartData = (assetData: any, assetType: string) => {
    const chartData: ChartData<"bar"> = {
        labels: assetData.map((item: any) => item.deviceType),
        datasets: [
            {
                label: getAssetTypeLabel(assetType),
                data: assetType === AssetTypeEnum["All assets"] ?
                    assetData.map((item: any) => item.countOfAllDeviceType) :
                    assetData.map((item: any) => item.countOfMaintenanceStarted),
                backgroundColor: colors[assetType as keyof typeof colors],
                barThickness: "flex",
            },
        ]
    }
    return chartData;
}


type AssetChartAttribute = {
    assetType: "AllAssets" | "MaintenanceStarted" | null;
    station: string | null;
}

const initialAssetChartAttribute: AssetChartAttribute = {
    assetType: null,
    station: null
}

const AssetWidget = ({ selectedStations }: { selectedStations: string }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const selectedCustomersList = useSelector(selectedCustomersSelector);
    const permittedCustomerIds = useMemo(() => {
        const _list = selectedCustomersList;
        if (!_list) return [];
        return _list.map((customer) => customer.customerId);
    }, [selectedCustomersList]);
    const selectedCustomersListByProp = useMemo(() => {
        return selectedStations.split(",")
    }, [selectedStations]);

    const singleCustomer = selectedCustomersListByProp.length === 1 ? selectedCustomersList?.find((customer) => customer.customerId === selectedCustomersListByProp[0]) : null;

    const [assetData, setAssetData] = useState<any[]>([]);
    const mainChartData = useMemo(() => {
        return getMainChartData(assetData);
    }, [assetData])
    const [assetStats, setAssetStats] = useState({
        allAssets: 0,
        maintenanceStarted: 0,
    })
    const childChartRef = useRef<any>(null);

    const [assetForStation, setAssetForStation] = useState<any>();
    const [assetChartAttribute, setAssetChartAttribute] = useState<AssetChartAttribute>(initialAssetChartAttribute);

    const [deviceTypeList, setDeviceTypeList] = useState<any[]>([]);
    const getDeviceTypes = () => {
        let url = `${APP_BASE_URL}${GET_DEVICE_TYPE_ASSET}`;
        axios.get(url).then((res) => {
            let deviceTypes = res.data.data.map((type: any) => ({
                value: type.typeid,
                label: type.type,
                alias: type.label,
            }))
            setDeviceTypeList(deviceTypes)
        });
    };

    useEffect(() => {
        getDeviceTypes();
    }, []);

    const mainChartOptions = useMemo(() => {
        const getMaxValue = () => {
            let y_max = 0;
            const values = (assetData || []).map((item: any) => (
                [item.allDevices, item.maintenanceStarted]
            ));

            const maxValue = Math.max(...values.flat(), 0);
            y_max = maxValue * 1.2;
            return y_max;
        }

        const y_max = getMaxValue();

        const options: ChartOptions<"bar"> = {
            responsive: true,
            maintainAspectRatio: false,
            onClick: (event, elements, chart) => {
                handleChartClick(event, elements, chart)
            },
            datasets: {
                bar: {
                    barPercentage: 0.5,
                    borderWidth: 2,
                    borderColor: 'rgba(255, 255, 255)',
                }
            },
            plugins: {
                legend: { position: 'top' },
                datalabels: {
                    anchor: 'end',
                    align: 'top',
                    font: { weight: 'bold' },
                    formatter: v => v?.toLocaleString?.()
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: { callback: v => v.toLocaleString() },
                    suggestedMax: y_max,
                },
            }
        };

        return options;
    }, [assetData])

    useEffect(() => {
        if (selectedCustomersListByProp.length > 0) {
            getAssetsData(selectedCustomersListByProp, "", "");
            setAssetForStation(null);
            setAssetChartAttribute(initialAssetChartAttribute);
        }

    }, [selectedCustomersListByProp]);

    const handleChartClick = async (_event: any, elements: any, chart: any) => {
        if (elements.length > 0) {
            const stationLabel = chart.tooltip?.dataPoints[0]?.label;
            const dataPointLabel = chart.tooltip?.dataPoints[0]?.dataset?.label;

            const stationCustomerId = selectedCustomersList.find((customer) =>
                customer.customerName === stationLabel
            )?.customerId;

            if (stationCustomerId) {
                await getAssetsData([stationCustomerId], stationLabel, dataPointLabel);
                if (childChartRef.current) {
                    console.log("scrolling")
                    childChartRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    };

    const getAssetsData = async (customerIds: string[], stationLabel: string, dataPointLabel?: string) => {
        try {
            const url = `${APP_BASE_URL}${GET_ALL_ASSETS}`;
            const params = {
                customerIds: customerIds.toString()
            }
            const res = await axios.get(url, { params });
            const data = res?.data?.data || [];
            if (singleCustomer && !stationLabel) {
                const dataPacket = {
                    customerName: singleCustomer.customerName,
                    allDevices: data.reduce((acc: number, curr: any) => acc + curr.countOfAllDeviceType, 0) || 0,
                    maintenanceStarted: data.reduce((acc: number, curr: any) => acc + curr.countOfMaintenanceStarted, 0) || 0
                }
                setAssetData([dataPacket]);
                setAssetStats({
                    allAssets: dataPacket.allDevices,
                    maintenanceStarted: dataPacket.maintenanceStarted
                });
                setAssetForStation(null);
                setAssetChartAttribute(initialAssetChartAttribute);
                return;
            }
            if (stationLabel) {
                // Set data for single station view
                setAssetForStation(data);
                // setAssetForStation(initialAssetForStation);
                setAssetChartAttribute({
                    assetType: AssetTypeEnum[dataPointLabel as keyof typeof AssetTypeEnum] || null,
                    station: stationLabel
                });
            } else {
                // clear child chart data
                setAssetForStation(null);
                setAssetChartAttribute(initialAssetChartAttribute);
                // set main chart data
                setAssetStats({
                    allAssets: data?.reduce?.((acc: number, curr: any) => acc + curr?.allDevices || 0, 0) || 0,
                    maintenanceStarted: data?.reduce?.((acc: number, curr: any) => acc + curr?.maintenanceStarted || 0, 0) || 0
                });
                setAssetData(data);
                // setAssetData(initialAssetData);
                // setAssetStats({
                //     allAssets: initialAssetData.reduce((acc: number, curr: any) => acc + curr.allDevices, 0) || 0,
                //     maintenanceStarted: initialAssetData.reduce((acc: number, curr: any) => acc + curr.maintenanceStarted, 0) || 0
                // });
            }

        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                toast.error(error?.response?.data?.message || "Something went wrong");
            } else {
                toast.error(error?.message || "Something went wrong");
            }
            console.log("ALL_ASSETS_DATA_ERROR", error);
        }
    }

    const redirectToAssetDetails = (event: any, elements: any, chart: any) => {
        try {
            const deviceLabel = chart.tooltip?.dataPoints[0]?.label;
            const dataPointLabel = chart.tooltip?.dataPoints[0]?.dataset?.label;
            const deviceType = deviceTypeList.find((deviceType) => deviceType.label === deviceLabel);
            console.log({ deviceLabel, dataPointLabel, deviceType, deviceTypeList });
            sessionStorage.setItem("selecetdAsstTab", "asset");
            if (dataPointLabel === "All assets") {
                dispatch(updateSelectedSidebarKey("ASSETS"));
                navigate(encodeURI(`/assets?station=${assetChartAttribute.station}&deviceType=${deviceType?.value}`));
            } else {
                dispatch(updateSelectedSidebarKey("ASSETS"));
                navigate(encodeURI(`/assets?station=${assetChartAttribute.station}&deviceType=${deviceType?.value}&maintenanceStatus=true`));
            }
        } catch (error: any) {
            console.log("REDIRECT_TO_ASSET_DETAILS_ERROR", error);
        }
    }

    const childChartData = useMemo(() => {
        if (!assetForStation) return null;
        return getChildChartData(assetForStation, assetChartAttribute.assetType || "");
    }, [assetForStation, assetChartAttribute.assetType]);

    const childChartOptions = useMemo(() => {
        let y_max = 0;
        if (assetChartAttribute.assetType === AssetTypeEnum["All assets"]) {
            y_max = Math.max(...(assetForStation || []).map((item: any) => item.countOfAllDeviceType))
        } else {
            y_max = Math.max(...(assetForStation || []).map((item: any) => item.countOfMaintenanceStarted))
        }
        y_max *= 1.2;

        const childChartOptions: ChartOptions<"bar"> = {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    left: 50,
                    right: 50,
                    top: 50,
                    bottom: 50
                }
            },
            onClick: (event, elements, chart) => {
                redirectToAssetDetails(event, elements, chart)
                // handleChartClick(event, elements, chart)
            },
            plugins: {
                legend: { display: false },
                datalabels: {
                    anchor: 'end',
                    align: 'end',
                    font: {
                        weight: 'bold',
                        size: 12,
                    },
                    formatter: v => v.toLocaleString(),
                    clamp: true,
                    clip: true,
                    textAlign: 'center',
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    suggestedMax: y_max,
                    ticks: { callback: v => v.toLocaleString() },
                },
            }
        };

        return childChartOptions;
    }, [assetForStation, assetChartAttribute.assetType])

    return (
        <>
            <div className="asset-widget">
                <div className="asset-widget-body">
                    <div className="asset-widget-body-header">
                        <div className="asset-widget-body-header-title">
                            <span className="asset-widget-body-header-title-text">Status of Asset Approval</span>
                            <div className="flex alignCenter gap-1">(
                                <span className="asset-widget-body-header-title-text all-asset-text">All Asset: {assetStats.allAssets} </span> |
                                <span className="asset-widget-body-header-title-text maintenance-started-text">Asset Maintenance Started: {assetStats.maintenanceStarted} </span>
                                )</div>
                        </div>
                    </div>
                    {mainChartData &&
                        <div style={{ height: "480px" }}>
                            <Chart
                                className="type-status-count-chart cursor-pointer"
                                type="bar"
                                options={mainChartOptions}
                                data={mainChartData}
                            />
                        </div>
                    }
                </div>
            </div>

            {(assetForStation && childChartData) &&
                <div className="asset-widget child-chart-container" ref={childChartRef}>
                    <div className="asset-widget-body">
                        <div className="asset-widget-body-header">
                            <div className="asset-widget-body-header-title">
                                <span className="asset-widget-body-header-title-text" style={{ color: colors[assetChartAttribute.assetType as keyof typeof colors] }}>
                                    {/* {assetChartAttribute.station ? `Asset Details for ${assetChartAttribute.station}` : 'Select a station to view details'} */}
                                    {assetChartAttribute.station} - {getAssetTypeLabel(assetChartAttribute.assetType || '')}
                                </span>
                            </div>
                        </div>
                        <div style={{ height: "480px" }}>
                            <Chart
                                
                                className="type-status-count-chart cursor-pointer"
                                type="bar"
                                options={childChartOptions}
                                data={childChartData}
                            />
                        </div>
                    </div>
                </div>
            }
        </>
    )
}

export default AssetWidget;